import { useEffect, useMemo, useState } from 'react';

export interface BackgroundImageDraft {
  /** A newly chosen file, uploaded when the dialog is saved. */
  file: File | null;
  /** The existing image is to be removed on save. */
  removed: boolean;
  /** What the preview shows: the chosen file, else the existing image (unless removed). */
  previewUrl: string | null;
  setFile: (file: File | null) => void;
  remove: () => void;
}

/**
 * Pending background image of an editor dialog. Nothing reaches the server until "Save", so
 * Cancel really cancels; the preview of a chosen file is a local object URL.
 */
export function useBackgroundImageDraft(existingUrl: string | null): BackgroundImageDraft {
  const [file, setFileState] = useState<File | null>(null);
  const [removed, setRemoved] = useState(false);
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl],
  );
  return {
    file,
    removed,
    previewUrl: objectUrl ?? (removed ? null : existingUrl),
    setFile: (next) => {
      setFileState(next);
      if (next) setRemoved(false);
    },
    remove: () => {
      setFileState(null);
      setRemoved(true);
    },
  };
}
